import { Link } from "react-router-dom";
import { ArrowRight, Check, FileText, MapPin, Phone, ShieldCheck, Timer } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { IMG, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const ATOUTS = [
    {
        icon: ShieldCheck,
        title: "Expertise locale approfondie",
        text: "Notre équipe locale de professionnels possède une connaissance étendue et mise à jour dans tous les aspects de la serrurerie. Grâce à des années d'expérience, elle identifie et résout les problèmes de serrures de toutes sortes.",
    },
    {
        icon: Timer,
        title: "Service rapide et fiable",
        text: "Le temps, c'est de l'argent, surtout en cas d'urgence. Étant localisés dans votre région, nous garantissons une intervention rapide, généralement en moins de 30 minutes.",
    },
    {
        icon: FileText,
        title: "Devis transparent et détaillé",
        text: "Chez nous, la transparence est primordiale. Avant toute intervention, nous fournissons un devis clair, précis et détaillé : vous connaissez le coût total à l'avance, sans surprise désagréable.",
    },
];

const SERVICES_DETAIL = [
    {
        title: "Ouverture de porte",
        to: "/ouverture-de-porte",
        text: "Égarer ses clés est un désagrément que nous aimerions tous éviter. Si cela vous arrive, notre service d'ouverture de porte vous secourt avec des techniques modernes, sans causer de dommages.",
    },
    {
        title: "Dépannage express adapté",
        to: "/sos-serrurier",
        text: "De la serrure cassée à la clé coincée ou au remplacement complet, notre service de dépannage couvre tout. Nous évaluons la situation et proposons des solutions sur mesure.",
    },
    {
        title: "Services d'urgence 24/7",
        to: "/sos-serrurier",
        text: "Une urgence peut survenir à tout moment : notre équipe dédiée est disponible 24/7. Cambriolage ou clé cassée, nous intervenons immédiatement pour sécuriser votre domicile.",
    },
];

const ETAPES = [
    {
        title: "Évaluation initiale approfondie",
        text: "Dès le premier contact, nous prenons le temps d'évaluer vos besoins spécifiques en serrurerie pour vous offrir un service personnalisé et prévenir les problèmes futurs.",
    },
    {
        title: "Proposition de devis personnalisé",
        text: "Sur la base de l'évaluation, un devis complet et détaillé vous est envoyé pour examen et validation. Il couvre toutes les dépenses potentielles, sans frais cachés.",
    },
    {
        title: "Intervention rapide et efficace",
        text: "Après approbation du devis, nos experts se déplacent avec tous les outils nécessaires, avec efficacité et discrétion, pour un service de haute qualité.",
    },
];

const FAQ_LOCAL = [
    {
        q: "Comment savoir si le serrurier est fiable ?",
        a: "Tous nos artisans sont certifiés, assurés et expérimentés. Ils répondent à des critères de qualité stricts, garantissant leur fiabilité et leur compétence. Demandez leur numéro SIRET et leur assurance professionnelle avant toute intervention.",
    },
    {
        q: "Quels sont les tarifs pour les différents services ?",
        a: "L'ouverture d'une porte simplement claquée est à 60€ TTC. Le changement de serrure démarre à 85€. Un devis détaillé est toujours fourni avant intervention — vous connaissez le prix exact avant que l'artisan commence.",
    },
    {
        q: "Puis-je annuler une intervention ?",
        a: "Bien sûr, nous offrons des conditions d'annulation flexibles. Si vous n'avez pas encore signé le devis, l'annulation est sans frais. Consultez les détails dans votre devis pour connaître les éventuels frais d'annulation.",
    },
    {
        q: "Intervenez-vous 24h/24 et le week-end autour de chez moi ?",
        a: "Oui, notre service de dépannage serrurerie fonctionne 24h/24, 7j/7 dans tous les arrondissements de Paris et les villes d'Île-de-France. Que ce soit un vendredi soir, un dimanche ou un jour férié, un artisan qualifié sera chez vous en 30 minutes.",
    },
    {
        q: "Que faire en cas de clé cassée dans la serrure ?",
        a: "Ne forcez surtout pas. Appelez-nous immédiatement : nos serruriers disposent d'extracteurs professionnels pour retirer le fragment de clé sans abîmer le barillet. Si la serrure est endommagée, nous pouvons la remplacer dans la foulée.",
    },
    {
        q: "Faut-il fournir un justificatif pour ouvrir ma porte ?",
        a: "Oui, c'est obligatoire. Pour protéger les occupants légitimes, notre artisan vous demandera une pièce d'identité et un justificatif de domicile (facture EDF, quittance de loyer, contrat de bail). En l'absence de ces documents, nous ne pouvons pas intervenir.",
    },
];

const ZONES_LINKS = [
    { label: "Paris 1er-4e", to: "/serrurier-paris-1" },
    { label: "Paris 5e-8e", to: "/serrurier-paris-5" },
    { label: "Paris 9e-12e", to: "/serrurier-paris-9" },
    { label: "Paris 13e-16e", to: "/serrurier-paris-13" },
    { label: "Paris 17e-20e", to: "/serrurier-paris-17" },
    { label: "Boulogne-Billancourt", to: "/serrurier-boulogne-billancourt" },
    { label: "Vincennes", to: "/serrurier-vincennes" },
    { label: "Saint-Denis", to: "/serrurier-saint-denis" },
    { label: "Versailles", to: "/serrurier-versailles" },
    { label: "Neuilly-sur-Seine", to: "/serrurier-neuilly-sur-seine" },
    { label: "Montreuil", to: "/serrurier-montreuil" },
    { label: "Créteil", to: "/serrurier-creteil" },
];

export default function SerrurierAutourDeMoi() {
    return (
        <div data-testid="autour-de-moi-page">
            <SEO
                title="Serrurier Autour de Moi — Dépannage & Ouverture de Porte 30 min | Paris"
                description="Serrurier autour de moi : service complet de dépannage, installation et réparation en serrurerie. Intervention rapide en moins de 30 min, 24h/24 et 7j/7, devis transparent. ☎ 06 25 25 86 19"
                path="/serrurier-autour-de-moi"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Serrurier autour de moi</span>
                    </nav>
                    <SectionLabel dark>Serrurerie de proximité</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Serrurier autour de moi :"]} />
                        <MaskedLines delay={0.45} lines={["la confiance, à proximité."]} lineClassName="text-gold" />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Vous cherchez un serrurier fiable près de chez vous ? Notre service localisé est le choix
                            préféré de nombreux résidents parisiens et franciliens pour tous les besoins en serrurerie.
                        </p>
                        <a
                            href={PHONE_TEL}
                            data-testid="autour-call-button"
                            className="mt-8 inline-flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {/* ATOUTS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-5 md:grid-cols-3">
                    {ATOUTS.map((a, i) => (
                        <FadeUp key={a.title} delay={i * 0.08}>
                            <div className="h-full rounded-xl border border-midnight/10 bg-white p-8 shadow-[0_24px_60px_-28px_rgba(10,17,40,0.18)]" data-testid={`autour-atout-${i}`}>
                                <a.icon className="h-8 w-8 text-emergency" strokeWidth={1.8} />
                                <h2 className="mt-5 font-display text-xl font-extrabold tracking-tight">{a.title}</h2>
                                <p className="mt-3 text-sm leading-relaxed text-midnight/60">{a.text}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* SERVICES + IMAGE */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <FadeUp>
                        <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-cloud p-10 shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]">
                            <img
                                src={IMG("serrurier-autour-de-moi.webp")}
                                alt="Serrurier autour de moi — illustration artisan et clé dorée"
                                className="max-h-full object-contain"
                                width="800"
                                height="600"
                                loading="lazy"
                            />
                        </div>
                    </FadeUp>
                    <div>
                        <FadeUp>
                            <SectionLabel>Nos services en détail</SectionLabel>
                            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                                Une gamme complète, près de chez vous
                            </h2>
                        </FadeUp>
                        <div className="mt-8 space-y-5">
                            {SERVICES_DETAIL.map((s) => (
                                <FadeUp key={s.title}>
                                    <Link to={s.to} className="group flex items-start justify-between gap-4 border-l-2 border-gold bg-cloud p-6 transition-colors hover:bg-white">
                                        <div>
                                            <h3 className="font-display text-lg font-extrabold tracking-tight">{s.title}</h3>
                                            <p className="mt-2 text-sm leading-relaxed text-midnight/60">{s.text}</p>
                                        </div>
                                        <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-emergency transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </Link>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PROCESSUS */}
            <section className="grain bg-midnight py-16 text-white md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel dark>Le processus</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            3 étapes faciles
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px bg-white/10 md:grid-cols-3">
                        {ETAPES.map((e, i) => (
                            <FadeUp key={e.title} delay={i * 0.08} className="bg-midnight">
                                <div className="h-full p-8" data-testid={`autour-etape-${i}`}>
                                    <span className="font-display text-5xl font-extrabold text-gold/40">{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="mt-4 font-display text-xl font-extrabold tracking-tight">{e.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/60">{e.text}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>


            {/* ZONES */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Secteurs couverts</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        Trouvez votre serrurier autour de vous
                    </h2>
                    <p className="mt-6 max-w-2xl leading-relaxed text-midnight/70">
                        Nos artisans couvrent l'intégralité des 20 arrondissements de Paris ainsi que les principales villes d'Île-de-France. Cliquez sur votre secteur pour accéder à la page dédiée avec les tarifs et délais précis.
                    </p>
                </FadeUp>
                <div className="mt-8 flex flex-wrap gap-3" data-testid="autour-zones">
                    {ZONES_LINKS.map((z) => (
                        <Link
                            key={z.to}
                            to={z.to}
                            className="flex items-center gap-2 rounded-lg border border-midnight/10 bg-white px-4 py-2.5 text-sm font-semibold text-midnight/70 transition-all duration-200 hover:-translate-y-0.5 hover:border-emergency hover:text-emergency hover:shadow-lg"
                        >
                            <MapPin className="h-3.5 w-3.5 text-emergency" /> {z.label}
                        </Link>
                    ))}
                </div>
                <FadeUp delay={0.12}>
                    <div className="mt-8 border-l-2 border-gold bg-cloud p-7">
                        <p className="font-bold text-midnight">Votre ville ne figure pas dans la liste ?</p>
                        <p className="mt-2 text-sm leading-relaxed text-midnight/70">
                            Notre zone d'intervention couvre toute l'Île-de-France — bien au-delà des liens affichés. Appelez-nous : si un artisan peut intervenir chez vous dans un délai raisonnable, nous vous l'envoyons immédiatement.
                        </p>
                    </div>
                </FadeUp>
            </section>

            {/* FAQ LOCALE */}
            <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
                <FadeUp>
                    <SectionLabel>Questions fréquentes</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        On répond à tout
                    </h2>
                </FadeUp>
                <div className="mt-10 space-y-4">
                    {FAQ_LOCAL.map((f, i) => (
                        <FadeUp key={f.q} delay={i * 0.06}>
                            <div className="rounded-xl border border-midnight/10 bg-white p-7" data-testid={`autour-faq-${i}`}>
                                <h3 className="flex items-start gap-3 font-display text-lg font-extrabold tracking-tight">
                                    <Check className="mt-1 h-5 w-5 shrink-0 text-emergency" /> {f.q}
                                </h3>
                                <p className="mt-3 pl-8 text-sm leading-relaxed text-midnight/60">{f.a}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
                <FadeUp delay={0.1}>
                    <p className="mt-10 flex items-center justify-center gap-2 text-sm font-semibold text-midnight/60">
                        <MapPin className="h-4 w-4 text-emergency" /> Intervention en moins de 30 minutes partout à Paris et en Île-de-France
                    </p>
                </FadeUp>
            </section>

            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Votre serrurier de proximité arrive en 30 min.
                        </h2>
                        <p className="mt-3 text-white/60">Devis transparent avant chaque intervention. 24h/24 — 7j/7.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="autour-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
